/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {AdditionalFeatures, treeCheckBox} from '../../filters/Filter';
import useMedia from '../../hooks/useMedia';
import {DLSTheme} from '../../types/types';
import {PrimaryButton} from '../buttons/PrimaryButton';
import {TertiaryButton} from '../buttons/TertiaryButton';
import {ButtonSpinner} from '../loading/ButtonSpinner';
import SelectMegaMenu from './SelectMegaMenu';

export interface FilterMegaMenuProps {
  children?: string | JSX.Element;
  elementId?: string;
  onMenuClose?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  selectFieldValue: string | JSX.Element;
  selectFieldMinWidth?: string;
  selectFieldMaxWidth?: string;
  loading?: boolean;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  actionLabel?: string;
  onActionClick?: Function;
  menuLeft?: string;
  menuWidth?: string;
  minMenuWidth?: string;
  menuHeight?: string;
  menuPaddingBottom?: string;
  numberOptionsSelected?: number;
  disabled?: boolean;
  hideActionButtons?: boolean;
  shouldCloseMenu?: boolean;
  setMenuCurrentState?: (state: boolean) => void;
  menuRight?: string;
  additionalFeatures?: AdditionalFeatures
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  menuContent : {
    [theme.breakpoints.down('sm')] : {
      padding : '0px 24px 4px 24px'
    }
  },
  actionButtons : {
    display    : 'flex',
    alignItems : 'center'
  },
  applyButton : {
    width                          : '286px',
    marginRight                    : '15px',
    [theme.breakpoints.down('sm')] : {
      width       : '100%',
      marginRight : '0px'
    }
  },
  additionalFeature : {
    display                                              : 'flex',
    flexWrap                                             : 'wrap',
    right                                                : '0px',
    position                                             : 'absolute',
    '@media (min-height: 460px) and (max-height: 530px)' : {
      position : 'relative',
      right    : '-9px'
    }
  }
}));

/**
 * Component more specific to the Filters that utilizes the
 * SelectMegaMenu and provides call to actions within it
 *
 * ```typescript
 *
 * import {FilterMegaMenu} from '@ion/dls/src/components/menus';
 *
 * <FilterMegaMenu loading={loading}
 *   selectFieldValue={<SelectFieldValue />}
 *   onMenuClose={onMenuClose}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   primaryButtonDisabled={isDisabledForm}>
 *   <div>HELLO</div>
 * </FilterMegaMenu>
 *
 * ```
 *
 */
export const FilterMegaMenu = ({
  children,
  elementId,
  disabled,
  onMenuClose,
  onCancel,
  onSubmit,
  selectFieldValue,
  selectFieldMinWidth,
  selectFieldMaxWidth,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  menuLeft,
  menuRight,
  menuWidth,
  minMenuWidth,
  menuHeight,
  menuPaddingBottom,
  numberOptionsSelected,
  actionLabel,
  onActionClick,
  additionalFeatures,
  setMenuCurrentState,
  loading = false,
  shouldCloseMenu = false,
  hideActionButtons = false
}: FilterMegaMenuProps) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const {isPhone} = useMedia();

  useEffect(() => {
    if (shouldCloseMenu) {
      setMenuOpen(false);
    }
  }, [shouldCloseMenu]);

  useEffect(() => {
    setMenuCurrentState?.(menuOpen);
  }, [menuOpen]);

  function handleSubmit() {
    setMenuOpen(false);
    onSubmit?.();
  }

  function handleCancel() {
    setMenuOpen(false);
    onCancel?.();
  }

  function handleActionClick(e) {
    setMenuOpen(false);
    onActionClick?.(e);
  }

  return (
    <SelectMegaMenu selectFieldValue={selectFieldValue}
      elementId={elementId}
      disabled={disabled}
      numberOptionsSelected={numberOptionsSelected}
      onMenuClose={onMenuClose}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      selectFieldMinWidth={selectFieldMinWidth}
      selectFieldMaxWidth={selectFieldMaxWidth}
      menuLeft={menuLeft}
      menuRight={menuRight}
      menuWidth={menuWidth}
      minMenuWidth={minMenuWidth}
      menuHeight={menuHeight}
      menuPaddingBottom={menuPaddingBottom}>
      <div className={classes.menuContent}>
        {children}
        {!hideActionButtons && (
          <div className={classes.actionButtons}>
            <div className={classes.applyButton}>
              <PrimaryButton fullWidth
                disabled={loading || primaryButtonDisabled}
                onClick={handleSubmit}>
                {
                  loading ? (
                    <ButtonSpinner />
                  ) : <>{translate('common:editors.shared.done')}</>
                }
              </PrimaryButton>
            </div>
            {!isPhone && (
              <TertiaryButton disabled={secondaryButtonDisabled}
                onClick={handleCancel}>
                {translate('common:editors.shared.cancel')}
              </TertiaryButton>
            )}
            {!isPhone && !!actionLabel && (
              <TertiaryButton disabled={loading}
                onClick={handleActionClick}>
                {actionLabel}
              </TertiaryButton>
            )}
            {additionalFeatures?.permission && (
              <div className={classes.additionalFeature}>
                {treeCheckBox(additionalFeatures)}
              </div>
            )}
          </div>
        )}
      </div>
    </SelectMegaMenu>
  );
};

export default FilterMegaMenu;
